import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepIconProps,
  StepLabel,
  Stepper,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { Option } from "../../../../constants/common";
// import { useProviderGroupList } from "../../../../queries/use-get-provider-groups";
import { getStoredRealm } from "@/utils/auth-storage";
import patientTemplateSample from "../../../../assets/admin/provider-group/patientTemplateSample.svg";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useUploadCsv } from "../../../../queries/use-upload-csv";
import {
  DownloadTemplateCategoryEnum,
  DownloadTemplateTypeEnum,
  MigrationControllerApi,
  UploadFileCategoryEnum,
} from "../../../../services";
import { getViewAs } from "../../../../utils/auth-storage";
import {
  getConfigObject,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../utils/utils";
import CustomSelect from "../../../CustomSelect";
import Flex from "../../../Flex";
import SideSheetDrawer from "../../../SidesheetDrawer";
import Text from "../../../Text";
import DropZone from "../../DropZone";

interface StaffUserModalProps {
  open: boolean;
  modalTitle: "Import Patients" | "Upload";
  onClose: () => void;
  hidePreviewStep?: boolean;
}

// Provider and Patient bulk import are no longer offered on the Data Import
// screen; past records of those types stay viewable through ENTITY_TYPE_MAPPER.
export const EntityType = [
  // { value: "PROVIDER", label: "Provider Data" },
  { value: "CPT", label: "CPT Code" },
  { value: "DRUG_CATALOG", label: "Drug Catalog" },
  { value: "ICD", label: "ICD 10 Code" },
  { value: "HCPCS", label: "HCPCS Code" },
  { value: "LOINC", label: "LOINC Code" },
  // { value: "PATIENT", label: "Patient Data" },
  { value: "LAB_TEST", label: "Lab Test" },
  { value: "IMAGING_STUDY", label: "Imaging Study" },
  { value: "PAYER_CATALOG", label: "Payer" },
];

const ENTITY_RECORD = {
  // PROVIDER: "Sample Provider Data",
  CPT: "Sample CPT Code",
  LOINC: "Sample LOINC Code",
  DRUG_CATALOG: "Sample Drug Catalog",
  ICD: "Sample ICD 10 Code",
  HCPCS: "Sample HCPCS Code",
  PATIENT: "Sample Patient Data",
  PAYER_CATALOG: "Sample Payer",
  LAB_TEST: "Sample Lab Test",
  IMAGING_STUDY: "Sample Imaging Study",
} as Record<string, string>;

const initialValues: UploadDataForm = {
  file: null,
  providerGroupUuid: undefined,
  type: undefined,
};

type UploadDataForm = {
  file?: File | null;
  providerGroupUuid?: string;
  type: DownloadTemplateCategoryEnum | undefined;
};

// Custom StepIcon component for hollow circle with number
const CustomStepIcon = (props: StepIconProps) => {
  const theme = useTheme();
  const { icon } = props;

  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        fontWeight: "500",
        color: theme.palette.primary.main,
        boxSizing: "border-box", // This ensures border is included in the width
      }}
    >
      {icon}
    </Box>
  );
};

const UploadData = ({
  open,
  modalTitle,
  onClose,
  hidePreviewStep,
}: StaffUserModalProps) => {
  const queryClient = useQueryClient();
  const view = getViewAs();
  const realm = getStoredRealm();

  const validationSchema = Yup.object({
    type: Yup.string()
      .when([], {
        is: () => modalTitle === "Upload",
        then: (schema) => schema.required("Entity Type is a required field"),
        otherwise: (schema) => schema.notRequired(),
      })
      .label("Entity Type"),
    // Provider Group was only required for the removed Provider/Patient imports.
    // providerGroupUuid: Yup.string()
    //   .when("type", {
    //     is: (value: DownloadTemplateCategoryEnum) =>
    //       (value === "PROVIDER" || value === "PATIENT") && view !== "provider",
    //     then: (schema) => schema.required("Provider Group is a required field"),
    //     otherwise: (schema) => schema.notRequired(),
    //   })
    //   .label("Provider Group"),
    file: Yup.mixed()
      .required("File is required")
      .test(
        "fileType",
        "Unsupported file format. Please upload a valid file. csv or xls",
        (value: any) => {
          if (!value) return false;
          const supportedFormats = [
            "text/csv", // .csv
            "application/vnd.ms-excel", // .xls
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          ];

          return supportedFormats.includes(value.type);
        }
      )
      // .test(
      //   "fileSize",
      //   "File size is too large. Maximum size is 2MB.",
      //   (value: any) => {
      //     if (!value) return false; // Required check
      //     const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      //     return value.size <= maxSize; // File size check
      //   }
      // )
      .label("File"),
  });

  const formik = useFormik<UploadDataForm>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // const [searchKey, setSearchKey] = useState<string>("");
  // Bumping this key remounts the DropZone (and its hidden file input) so a
  // re-upload of the same file in the same modal session fires onChange again.
  const [dropZoneKey, setDropZoneKey] = useState<number>(0);
  const { mutateAsync: uploadCsvFile } = useUploadCsv();

  const showSnackbar = useSnackbar();
  // Provider Group list was only needed by the removed Provider/Patient imports.
  // const { data: providerGroupList } = useProviderGroupList({
  //   pagination: DEF_PAGINATION,
  //   searchKey: searchKey,
  //   xTENANTID: view === "provider" ? (realm as string) : undefined,
  //   enabled: open,
  // });

  function handleSubmit(values: UploadDataForm) {
    let fileName = values?.file?.name;

    if (
      values?.file?.name?.includes(".xlsx") ||
      values?.file?.name?.includes(".xls")
    )
      fileName += " xl";

    if (!values.file) return;

    uploadCsvFile({
      title: fileName ?? "",
      file: values.file,
      type: values.type,
      providerGroupUuid: values.providerGroupUuid,
      xTENANTID: view === "provider" ? (realm as string) : undefined,
    })
      .then((res) => {
        queryClient.invalidateQueries({ queryKey: ["data-import"] });
        showSnackbar(getSuccessSnackbar(res));
        closeAndReset();
      })
      .catch((err) => {
        queryClient.invalidateQueries({ queryKey: ["data-import"] });
        const errorSnackbar = getErrorSnackbar(err);
        if (!errorSnackbar.message) showSnackbar(errorSnackbar);
        // Remount the DropZone so the same file can be re-picked; the kept file is shown via `value`.
        setDropZoneKey((prev) => prev + 1);
        formik.setFieldError("file", errorSnackbar.message);
      });
  }

  function getCSVTemplate(type: DownloadTemplateTypeEnum) {
    new MigrationControllerApi(getConfigObject())
      .downloadTemplate(
        modalTitle === "Import Patients"
          ? "PATIENT"
          : (formik.values?.type as any),
        type,
        undefined, // xTENANTID
        { responseType: "blob" }
      )
      .then((res) => {
        const blobFile = res?.data;

        const fileUrl = URL.createObjectURL(blobFile);

        const fileName = `${
          ENTITY_RECORD[
            modalTitle === "Import Patients" ? "PATIENT" : formik.values.type!
          ]
        }${type === "XLSX" ? " xl" : ""}.${type === "XLSX" ? "xlsx" : "csv"}`;

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(fileUrl);
      });
  }

  function closeAndReset() {
    onClose();
    formik.resetForm();
    formik.setFieldValue("provider", "");
    // Force a fresh DropZone (and file input) for the next modal session.
    setDropZoneKey((prev) => prev + 1);
  }

  const stepsData: Option[] = [
    {
      value: "download",
      label: "Download CSV File",
      component: (
        <Flex gap={1}>
          <Button
            disabled={modalTitle === "Upload" && !formik.values.type}
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => getCSVTemplate("CSV")}
          >
            Download Sample CSV Template
          </Button>

          <Button
            disabled={modalTitle === "Upload" && !formik.values.type}
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => getCSVTemplate("XLSX")}
          >
            Download Sample XLSX Template
          </Button>
        </Flex>
      ),
    },
    {
      value: "preview",
      label: "Add or Edit info in CSV Template",
      component: (
        <img
          style={{ width: "600px" }}
          src={patientTemplateSample}
          alt="sample-patient-template"
          width={150}
        />
      ),
    },
    {
      value: "upload",
      label: "Upload CSV File",
      component: (
        <Box
          sx={{
            width: "100%",
          }}
        >
          <DropZone
            key={dropZoneKey}
            error={formik.touched.file && formik.errors.file}
            height={"121px"}
            content=""
            accept={[".csv", ".xlsx"]?.join(",")}
            value={formik.values.file?.name}
            fileName={formik.values.file?.name}
            onClick={(files) => {
              if (!files) {
                formik.setFieldValue("file", null);
                return;
              }
              files.item(0) && formik.setFieldValue("file", files.item(0));
            }}
            titleComponent={
              <Box
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Text sx={{ textAlign: "center" }}>
                  <Text
                    component="span"
                    sx={{ color: "primary.main", fontWeight: 500 }}
                  >
                    Click to upload
                  </Text>
                  <Text component="span" sx={{ color: "text.secondary" }}>
                    {" "}
                    or drag and drop
                  </Text>
                </Text>
                <Text
                  sx={{
                    textAlign: "center",
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  CSV or XLSX
                </Text>
              </Box>
            }
          />
        </Box>
      ),
    },
  ];

  return (
    <SideSheetDrawer
      onClose={closeAndReset}
      open={open}
      title={modalTitle}
      w="50vw"
      footerActions={
        <Button onClick={async () => formik.submitForm()} variant="contained">
          {modalTitle === "Import Patients" ? "Import" : "Upload"}
        </Button>
      }
    >
      {modalTitle === "Import Patients" ? (
        <ExtraInformation />
      ) : (
        <Grid container spacing={2}>
          <Grid size={12}>
            <CustomSelect
              required
              name="type"
              handleBlur={formik.handleBlur}
              label="Entity Type"
              options={EntityType}
              placeholder="Select"
              value={formik.values.type}
              onChange={(v) => {
                formik.setFieldValue("type", v as UploadFileCategoryEnum);
                formik.setFieldValue(
                  "providerGroupUuid",
                  initialValues.providerGroupUuid
                );
              }}
              error={formik.touched.type && formik.errors.type}
            />
          </Grid>
          {/* Provider Group only applied to the removed Provider/Patient imports.
          {(formik.values.type === "PATIENT" ||
            formik.values.type === "PROVIDER") &&
            view !== "provider" && (
              <Grid item xs={12}>
                <CustomSelect
                  required
                  handleBlur={formik.handleBlur}
                  name="providerGroupUuid"
                  label="Provider Group"
                  placeholder="Select Provider Group"
                  onSearch={(_, val) => setSearchKey(val)}
                  options={
                    providerGroupList?.content?.map((it) => ({
                      label: it.name,
                      value: it.uuid,
                    })) ?? []
                  }
                  value={formik.values.providerGroupUuid}
                  onChange={(v) => formik.setFieldValue("providerGroupUuid", v)}
                  error={
                    formik.touched.providerGroupUuid &&
                    formik.errors.providerGroupUuid
                  }
                />
              </Grid>
            )} */}
        </Grid>
      )}
      <Stepper
        orientation="vertical"
        sx={{
          width: "100%",
          marginTop: "20px",
          "& .MuiStepConnector-root": {
            left: "15px", // Centered: half of icon width (32px / 2 = 16px) - 1px adjustment
          },
          "& .MuiStepLabel-root": {
            padding: 0,
            "& .MuiStepLabel-iconContainer": {
              padding: 0,
              marginRight: "12px",
            },
          },
        }}
      >
        {(hidePreviewStep
          ? stepsData?.filter((it) => it?.value !== "preview")
          : stepsData
        )?.map((step, index) => (
          <Step key={step.label} active expanded>
            <StepLabel
              sx={{
                "& .MuiStepLabel-iconContainer": {
                  padding: 0,
                },
              }}
              slots={{
                stepIcon: (props) => (
                  <CustomStepIcon
                    {...props}
                    active={props.active || props.completed}
                    completed={false}
                    icon={String(index + 1).padStart(2, "0")}
                    sx={{ padding: 0, margin: 0, width: 32, height: 32 }}
                  />
                ),
              }}
            >
              <Text
                sx={{ color: "#1A1A1A80", fontSize: "14px", fontWeight: "500" }}
              >
                {step.label}
              </Text>
            </StepLabel>
            <StepContent onClick={() => {}}>{step.component}</StepContent>
          </Step>
        ))}
      </Stepper>
    </SideSheetDrawer>
  );
};

export default UploadData;

const ExtraInformation = () => (
  <Box>
    <Text fontWeight={"medium"} c="gray">
      Please select a Comma Separated Values file with data arranged in below
      format.
    </Text>
    <Text variant="subtitle2">
      First Name, Last Name, Gender, Date Of Birth(mm/dd/yyyy), Email, Home
      Phone
    </Text>
    <Text variant="subtitle2">
      Mobile Phone, Work Phone, AddressLine 1, AddressLine2, City, Country,
      State
    </Text>
    <Text variant="subtitle2">
      ZIP Code, Caregiver First Name, Caregiver Last Name.
    </Text>
    <Text variant="subtitle2">
      First Name, Last Name, Gender, Date Of Birth should not be empty.
    </Text>
    <Text mt={"2rem"}>*YOU CAN ONLY IMPORT A MAXIMUM OF 500 PATIENTS.</Text>
  </Box>
);
